import { ClockIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Input, Modal } from "ui";

import { useAddMarketingFlowStep, useUpdateMarketingFlowStep } from "../../apis";
import {
  EMarketingChannel,
  MarketingFlowStep,
  MarketingFlowStepData
} from "../../marketing.types";
import { MessageComposer, MessageComposerValue } from "../MessageComposer";

import styles from "./FlowStepModal.module.scss";

interface FlowStepModalProps {
  isOpen: boolean;
  flowId: string;
  editableStep: MarketingFlowStep | null;
  onClose: () => void;
}

const AUTO_CHANNEL_VALUE = "auto";

const FlowStepModal = ({
  isOpen,
  flowId,
  editableStep,
  onClose
}: FlowStepModalProps) => {
  const { t } = useTranslation();

  const [delayDays, setDelayDays] = useState<number>(7);
  const [composer, setComposer] = useState<MessageComposerValue>({
    messageText: "",
    templateName: "",
    templateParams: [],
    channel: AUTO_CHANNEL_VALUE
  });

  useEffect(() => {
    if (isOpen) {
      setDelayDays(editableStep?.delayDays ?? 7);
      setComposer({
        messageText: editableStep?.messageText ?? "",
        templateName: editableStep?.templateName ?? "",
        templateParams: editableStep?.templateParams ?? [],
        channel: editableStep?.channel ?? AUTO_CHANNEL_VALUE
      });
    }
  }, [isOpen, editableStep]);

  const { mutate: addStep, isLoading: isAdding } = useAddMarketingFlowStep({
    onSuccess: () => {
      toast.success(t("Step added"));
      onClose();
    },
    onError: toastServerError
  });

  const { mutate: updateStep, isLoading: isUpdating } =
    useUpdateMarketingFlowStep({
      onSuccess: () => {
        toast.success(t("Step updated"));
        onClose();
      },
      onError: toastServerError
    });

  const handleOk = () => {
    if (!composer.messageText.trim() && !composer.templateName) {
      toast.error(t("Provide a message text or an approved template"));
      return;
    }

    if (
      composer.templateName &&
      composer.templateParams.some((param) => !param.trim())
    ) {
      toast.error(t("Fill in all template variables"));
      return;
    }

    const data: MarketingFlowStepData = {
      delayDays,
      channel:
        composer.channel === AUTO_CHANNEL_VALUE
          ? undefined
          : (composer.channel as EMarketingChannel),
      messageText: composer.messageText.trim() || undefined,
      templateName: composer.templateName || undefined,
      templateParams: composer.templateName
        ? composer.templateParams
        : undefined
    };

    if (editableStep) {
      updateStep({ flowId, stepId: editableStep.id, data });
    } else {
      addStep({ flowId, data });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={editableStep ? t("Edit step") : t("Add step")}
      contentClassName={styles.modalContent}
      isLoading={isAdding || isUpdating}
      okText={editableStep ? t("Save") : t("Add")}
      onClose={onClose}
      onOk={handleOk}
    >
      <div className={styles.form}>
        <Input
          label={t("Delay (days) from the previous step, or from enrollment for the first step")}
          type="number"
          onlyInteger
          action={<ClockIcon width={18} height={18} />}
          value={delayDays}
          onChange={(event) => setDelayDays(Number(event.target.value) || 0)}
        />

        <MessageComposer value={composer} onChange={setComposer} />
      </div>
    </Modal>
  );
};

export default FlowStepModal;
